import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button, Row, Col, Spinner } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";

function AddTesto() {
  const baseURL = process.env.REACT_APP_API_URL;
  const { id } = useParams(); // ✅ edit mode via param
  const navigate = useNavigate();
  const token = localStorage.getItem("profileToken");

  const initialState = { text: "", brand_id: "" };

  const [formData, setFormData] = useState(initialState);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingBrands, setLoadingBrands] = useState(false);

  // ✅ Fetch brands
  useEffect(() => {
    (async () => {
      setLoadingBrands(true);
      try {
        const { data } = await axios.get(`${baseURL}/brand/list`, {
          headers: { "x-access-token": token || "" },
        });
        setBrands(data.status && Array.isArray(data.result) ? data.result : []);
      } catch (err) {
        console.error("❌ Error fetching brands:", err);
        Swal.fire("Error", "Failed to fetch brands", "error");
      } finally {
        setLoadingBrands(false);
      }
    })();
  }, [baseURL, token]);

  // ✅ Fetch testimonial for edit
  useEffect(() => {
    if (!id) return;

    (async () => {
      try {
        const { data } = await axios.get(`${baseURL}/testimonial/${id}`, {
          headers: { "x-access-token": token || "" },
        });

        if (data.status && data.result) {
          const t = data.result;
          setFormData({
            text: t.text || "",
            brand_id: t.brand_id ? String(t.brand_id) : "",
          });
        } else {
          Swal.fire("Error", data.message || "Testimonial not found", "error");
          navigate("/testimonial-list");
        }
      } catch (err) {
        console.error("❌ Error fetching testimonial:", err);
        Swal.fire("Error", "Failed to fetch testimonial", "error");
        navigate("/testimonial-list");
      }
    })();
  }, [id, baseURL, token, navigate]);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleReset = () => setFormData(initialState);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        text: formData.text.trim(),
        brand_id: Number(formData.brand_id),
      };

      const headers = { "x-access-token": token || "" };

      if (id) {
        await axios.put(`${baseURL}/testimonial/update/${id}`, payload, { headers });
        await Swal.fire("Success", "✅ Testimonial updated successfully!", "success");
      } else {
        await axios.post(`${baseURL}/testimonial/add`, payload, { headers });
        await Swal.fire("Success", "✅ Testimonial added successfully!", "success");
      }

      navigate("/testimonial-list");
    } catch (err) {
      console.error("❌ Error saving testimonial:", err);
      Swal.fire("Error", "❌ Failed to save testimonial", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container my-4">
      <h3 className="fw-bold text-center mb-4 text-success">
        {id ? "Update Testimonial" : "Add New Testimonial"}
      </h3>

      <form onSubmit={handleSubmit}>
        <Row className="g-4">
          <Col xs={12} md={12}>
            <div className="border border-success rounded-4 p-4">
              <Row>
                <Col xs={12} md={12}>
                  <div className="mb-3">
                    <label htmlFor="text" className="form-label">
                      Text <span className="text-danger">*</span>
                    </label>
                    <textarea
                      id="text"
                      className="form-control"
                      placeholder="Write testimonial..."
                      rows={6}
                      value={formData.text}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </Col>
              </Row>

              <Row>
                <Col xs={12} md={6}>
                  <div className="mb-3">
                    <label htmlFor="brand_id" className="form-label">
                      Brand <span className="text-danger">*</span>
                    </label>
                    <select
                      id="brand_id"
                      className="form-select"
                      value={formData.brand_id}
                      onChange={handleChange}
                      disabled={loadingBrands}
                      required
                    >
                      <option value="">
                        {loadingBrands ? "Loading..." : "Select Brand"}
                      </option>
                      {brands.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </Col>

                <Col xs={12} md={6}>
                  <div className="d-flex justify-content-end gap-2 mt-4">
                    <Button
                      variant="outline-dark"
                      className="rounded-pill px-4"
                      onClick={handleReset}
                      type="button"
                    >
                      Reset
                    </Button>

                    <Button
                      variant="success"
                      className="rounded-pill px-4"
                      type="submit"
                      disabled={loading}
                    >
                      {loading ? <Spinner size="sm" /> : id ? "Update" : "Save"}
                    </Button>
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </form>
    </div>
  );
}

export default AddTesto;
